import React from "react";
import { DateBuilder, rng, ActivityListing, ActivityType, ActionPlanRiskConsequence, ActionPlanRiskLikelihood } from "../../../amorg-common";
import { ButtonRow, BorderRow } from "../../../designSystem/button/ButtonRow";
import { colors } from "../../../designSystem/colors/color";
import { PillWrapper, Pill } from "../../../designSystem/pill/Pill";
import { H2, H3 } from "../../../designSystem/typography/Heading";
import { ActivityGraph } from "../../../features/overview/ActivityGraph/ActivityGraph";
import { TimelineGraphYearRow } from "../../../designSystem/graphs/timelineGraph/TimelineGraphYearRow";
import { AnswerGraphics } from "../../../features/surveys/AnswerGraph";
import { getMonthStart } from "../../../utils/graphUtils";
import { SegmentBar } from "../../../designSystem/graphs/SegmentBar";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { SegmentRatioBar } from "../../../designSystem/graphs/SegmentRatioBar";
import { VerticalBarGraph } from "../../../designSystem/graphs/VerticalBarGraph";
import { Chip } from "../../../designSystem/Chip";
import { Icon, IconSize } from "../../../designSystem/icons/Icons";
import { RiskAssessmentEvaluator } from "../../../designSystem/graphs/RiskAssessmentEvaluator";
import { Avatar } from "../../../designSystem/avatar/Avatar";
import { TextAvatar, TextAvatarSizes } from '../../../designSystem/avatar/TextAvatar';
import { RadialChart } from "../../../designSystem/graphs/RadialChart";
import { RadialAvgChart } from "../../../designSystem/graphs/RadialAvgChart";
import { RadialPctChart } from '../../../designSystem/graphs/RadialPctChart';
import { CompletionPanel } from "../../../designSystem/graphs/CompletionPanel";
import { RadialPieChart } from "../../../designSystem/graphs/RadialPieChart";
import { IconAvatar } from "../../../designSystem/avatar/IconAvatar";
import { Small } from "../../../designSystem/typography/Body";
import { RiskAssessmentEvaluator2 } from '../../../designSystem/graphs/RiskAssessmentEvaluator2';
import { Badge } from "../../../designSystem/Badge";
import { DetailedRiskAssessmentEvaluator } from "../../../designSystem/graphs/DetailedRiskAssessmentEvaluator";
import { DialGauge } from "../../../designSystem/graphs/DialGauge";
import { DesignSystemPageSection, DesignSystemVariantSection } from "./DesignSystemPageSection";


export const GraphicsOverview = (): JSX.Element =>
{
    const [selectedAvatars, setSelectedAvatars] = React.useState<string[]>([]);

    // For testing
    const graphStartWithOffset = DateBuilder.now();
    const graphStart = new DateBuilder(getMonthStart(graphStartWithOffset.date));

    function handleSelectAvatar(size:string)
    {
        if (selectedAvatars.includes(size))
            setSelectedAvatars(selectedAvatars.filter(s => s !== size));
        else
            setSelectedAvatars([...selectedAvatars, size]);
    }

    function getActivityListing (data: { id: string, start, end, activityType: ActivityType, title: string, description: string; })
    {
        const a: ActivityListing = {
            ...data,
            invitationType: 'InviteOnly',
            location: 'Oslo',
            workgroupId: '1',
            workgroupName: 'Workgroup 1',
            owner: null,
            participantCount: 0,
        };
        return a;
    }

    const activities: ActivityListing[] = [
        getActivityListing({ id: '1', start: graphStart.addDays(3).date, end: graphStart.addMonths(1).date, activityType: 'WorkgroupMeeting', title: 'An A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '2', start: graphStart.addDays(13).date, end: graphStart.addMonths(1).addDays(10).date, activityType: 'WorkgroupMeeting', title: 'An A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '3', start: graphStart.addDays(23).date, end: graphStart.addMonths(3).date, activityType: 'WorkgroupMeeting', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '4', start: graphStart.addMonths(2).addDays(2).date, end: graphStart.addMonths(3).addDays(2).date, activityType: 'AnnualWorkgroupStatus', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '5', start: graphStart.addMonths(2).addDays(12).date, end: graphStart.addMonths(3).addDays(12).date, activityType: 'AnnualWorkgroupStatus', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '6', start: graphStart.addMonths(4).addDays(2).date, end: graphStart.addMonths(4).addDays(2).date, activityType: 'AnnualWorkgroupStatus', title: 'Another A activity', description: 'Lorem ipsum...' }),
        getActivityListing({ id: '7', start: graphStart.addMonths(1).addDays(8).date, end: graphStart.addMonths(1).addDays(45).date, activityType: 'WorkplaceAssessment', title: 'Another A activity', description: 'Lorem ipsum...' }),
    ];

    const [selected, setSelected] = React.useState(true);

    const [consequence, setConsequence] = React.useState<ActionPlanRiskConsequence>('1 - small');
    const [likelihood, setLikelihood] = React.useState<ActionPlanRiskLikelihood>('1 - unlikely');

    return (
        <Flex flexDirection="column" gap={16}>
            <ButtonRow variant={"left-aligned"}>
                <H2>Graphics</H2>
            </ButtonRow>

            <DesignSystemPageSection title="DetailedRiskAssessmentEvaluator">
                <div style={{width: "800px"}}>
                    <DetailedRiskAssessmentEvaluator
                        consequence={consequence} 
                        likelihood={likelihood} 
                        onChange={(consequence,likelihood)=>{
                            setConsequence(consequence); 
                            setLikelihood(likelihood);
                        }} />
                </div>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="RiskAssessmentEvaluator">
                <div style={{width: "600px"}}>
                    <RiskAssessmentEvaluator 
                        consequence={consequence} 
                        likelihood={likelihood} 
                        onChange={(consequence,likelihood)=>{
                            setConsequence(consequence); 
                            setLikelihood(likelihood);
                        }} />
                </div>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="RiskAssessmentEvaluator2">
                <div style={{width: "600px"}}>
                    <RiskAssessmentEvaluator2 
                        consequence={consequence} 
                        likelihood={likelihood} 
                        onChange={(consequence,likelihood)=>{
                            setConsequence(consequence); 
                            setLikelihood(likelihood);
                        }} />
                </div>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="DialGauge">
                <DesignSystemVariantSection title="2 / 4 segments, 200px">
                    <DialGauge
                        segments={4}
                        selectedSegments={2}
                        color="green"
                        widthPx={50}
                    />
                </DesignSystemVariantSection>
                <DesignSystemVariantSection title="1 / 3 segments, 100px">
                    <DialGauge
                        segments={3}
                        selectedSegments={1}
                        color="yellow"
                        widthPx={100}
                    />
                </DesignSystemVariantSection>
                <DesignSystemVariantSection title="5 / 10 segments, 200px">
                    <DialGauge
                        segments={10}
                        selectedSegments={5}
                        color="red"
                        widthPx={200}
                    />
                </DesignSystemVariantSection>
            </DesignSystemPageSection>

            <DesignSystemPageSection title="Badge">
                
                <DesignSystemVariantSection title="success + TT">
                    <Badge variant="success" count={5} tooltip="This is a success badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="pending + TT">
                    <Badge variant="pending" count={2} tooltip="This is a pending badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="warning + TT">
                    <Badge variant="warning" count={1} tooltip="This is a warning badge" />
                </DesignSystemVariantSection>
                    
                <DesignSystemVariantSection title="custom icon + TT">
                    <Badge icon="actions_unavailable" count={3} tooltip="This is a custom icon badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="custom color + TT">
                    <Badge color="purple" count={3} tooltip="This is a custom color badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="custom icon + color + TT">
                    <Badge icon="actions_unavailable" color="purple" count={3} tooltip="This is a custom icon and color badge" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="no count + TT">
                    <Badge variant="warning" tooltip="This is a warning badge without count" />
                </DesignSystemVariantSection>

                <DesignSystemVariantSection title="no TT">
                    <Badge variant="success" count={10} />
                </DesignSystemVariantSection>
            </DesignSystemPageSection>



            <BorderRow>
                <H3>Avatar</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <Avatar key={a} size={a}>
                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>

                <H3>Avatar with selected</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a,i) =>
                        <Avatar key={a} size={a}
                            selected={i%2===0}>

                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>

                <H3>Avatar with onClick and tooltip</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <Avatar key={a} size={a} colorGenerationString={a}
                            selected={selectedAvatars.includes(a)} 
                            identifier={a} 
                            toolTip={<span>Avatar size: {a}</span>}
                            onClick={handleSelectAvatar}>
                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>

                <H3>Avatar with onClick and tooltip and custom border</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <Avatar key={a} size={a} colorGenerationString={a}
                            selected={selectedAvatars.includes(a)} 
                            identifier={a} 
                            onClick={handleSelectAvatar}
                            borderColor="yellow">

                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>TextAvatar</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <TextAvatar key={a} size={a}
                            text={a.substring(0, 2) || 'N/A'} 
                        />
                    )}
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>IconAvatar</H3>
                <Flex gap={8} alignItems="center">
                    {[ [undefined,undefined], ['32px','small'], ['48px','medium'], ['72px','large']].map((a) =>
                        <Flex flexDirection="column" alignItems="center" key={a[0] + a[1]}>
                            <Small>{a[0] || 'undefined'} / {a[1] || 'undefined'}</Small>
                            <IconAvatar size={a[0]} iconSize={a[1] as IconSize}
                                icon="actions_unavailable"
                            />
                        </Flex>
                    )}
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>Chip</H3>
                <Flex flexDirection="column" gap={4}>
                    <Flex gap={4}>
                        <Chip>Default</Chip>
                        <Chip selected>Default</Chip>
                    </Flex>
                    <Flex gap={4}>
                        <Chip><Flex>span with content icon <Icon name="actions_unavailable" size="x-small" /></Flex></Chip>
                        <Chip selected><Flex>span with content icon <Icon name="actions_unavailable" size="x-small" /></Flex></Chip>
                    </Flex>
                    <Flex gap={4}>
                        <Chip onClick={() => alert('click chip!')}>chip with onClick</Chip>
                        <Chip selected onClick={() => alert('click chip!')}>chip with onClick</Chip>
                    </Flex>
                    <Flex gap={4}>
                        <Chip button={{ icon: 'actions_unavailable', onClick: () => alert('click button!') }}>chip with onClick button</Chip>
                        <Chip selected button={{ icon: 'actions_unavailable', onClick: () => alert('click button!') }}>chip with onClick button</Chip>
                    </Flex>
                    <Flex gap={4}>
                        <Chip onClick={() => alert('click chip!')} button={{ icon: 'actions_unavailable', onClick: () => alert('click button!') }}>chip with onClick and onClick button</Chip>
                        <Chip selected onClick={() => alert('click chip!')} button={{ icon: 'actions_unavailable', onClick: () => alert('click button!') }}>chip with onClick and onClick button</Chip>
                    </Flex>
                    <Flex gap={4}>
                        <Chip onClick={() => alert('click chip!')} button={{ icon: 'actions_unavailable', onClick: () => alert('click button!') }}>chip with onClick and onClick button and icon <Icon name="arrows_arrow_arrowForward" /></Chip>
                        <Chip selected onClick={() => alert('click chip!')} button={{ icon: 'actions_unavailable', onClick: () => alert('click button!') }}>chip with onClick and onClick button and icon <Icon name="arrows_arrow_arrowForward" /></Chip>
                    </Flex>
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>Pill</H3>
                <PillWrapper>
                    <Pill
                        identifier="p"
                        button={{
                            icon: 'arrows_arrow_arrowForward',
                            onClick: () => { alert('button was clicked'); }
                        }}>
                        This is a pill with a button
                    </Pill>

                    <Pill
                        identifier="p"
                        variant={selected ? 'selected' : 'default'}
                        button={{
                            icon: selected ? 'actions_minus' : 'actions_plus',
                            onClick: () =>
                            {
                                setSelected(!selected);
                            }
                        }}>
                        This pill changes state ({selected ? 'valgt' : 'ikke-valgt'})
                    </Pill>
                </PillWrapper>
            </BorderRow>

            <BorderRow>
                <H3>SegmentBar</H3>
                <Flex flexDirection="column" gap={10}>
                    <SegmentBar
                        totalSegments={4}
                        segments={[
                            { color: '#2A2' },
                            { color: '#2A2' },
                            { color: '#2A2' },
                        ]}
                    />
                    <SegmentBar
                        height={30}
                        emptyColor='black'
                        totalSegments={4}
                        segments={[
                            { color: '#CCC' },
                            { color: '#CCC' },
                            { color: '#CCC' },
                        ]}
                    />
                    <SegmentBar
                        totalSegments={4}
                        segments={[
                            { color: '#2A2' },
                            { color: '#2A2' },
                            { color: '#2A2' },
                            { color: '#2A2' }
                        ]}
                    />
                    <SegmentBar
                        totalSegments={6}
                        segments={[
                            { color: '#2A2' },
                            { color: '#2A2' }
                        ]}
                    />
                    <SegmentBar
                        totalSegments={6}
                        selectedIndex={1}
                        height={18}
                        selectionPxIncrease={6}
                        segments={[
                            { color: '#2A2', onClick: () => alert('clicked 1!') },
                            { color: '#2A2', onClick: () => alert('clicked 2!') }
                        ]}
                    />
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>VerticalBarGraph</H3>
                <div style={{ paddingBottom: '5px' }}>
                    <VerticalBarGraph barHeightPx={100} heightMode="max-value" data={[
                        { value: 0.5, color: colors.graphs.survey.answers.veryDissatisfied },
                        { value: 1, color: colors.graphs.survey.answers.dissatisfied },
                        { value: 4, color: colors.graphs.survey.answers.neutral },
                        { value: 20, color: colors.graphs.survey.answers.satisfied },
                        { value: 75, color: colors.graphs.survey.answers.verySatisfied, legendText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                    ]} />
                </div>
                <div style={{ paddingBottom: '5px' }}>
                    <VerticalBarGraph barHeightPx={100} heightMode="max-value" data={[
                        { value: 5, color: colors.graphs.survey.answers.veryDissatisfied },
                        { value: 10, color: colors.graphs.survey.answers.dissatisfied },
                        { value: 15, color: colors.graphs.survey.answers.neutral },
                        { value: 40, color: colors.graphs.survey.answers.satisfied },
                        { value: 30, color: colors.graphs.survey.answers.verySatisfied, legendText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                    ]} />
                </div>
                <div style={{ paddingBottom: '5px' }}>
                    <VerticalBarGraph barHeightPx={100} heightMode="total-sum" data={[
                        { value: 5, color: colors.graphs.survey.answers.veryDissatisfied },
                        { value: 10, color: colors.graphs.survey.answers.dissatisfied },
                        { value: 15, color: colors.graphs.survey.answers.neutral },
                        { value: 40, color: colors.graphs.survey.answers.satisfied },
                        { value: 30, color: colors.graphs.survey.answers.verySatisfied, legendText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                    ]} />
                </div>
            </BorderRow>

            <BorderRow>
                <H3>SegmentRatioBar</H3>
                <div style={{ paddingBottom: '5px' }}>
                    <SegmentRatioBar legend="classic" data={[
                        { value: 0.5, color: colors.graphs.survey.answers.veryDissatisfied },
                        { value: 1, color: colors.graphs.survey.answers.dissatisfied },
                        { value: 4, color: colors.graphs.survey.answers.neutral },
                        { value: 20, color: colors.graphs.survey.answers.satisfied },
                        { value: 75, color: colors.graphs.survey.answers.verySatisfied, valueText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                    ]} />
                </div>

                <SegmentRatioBar legend="regular" showValueText data={[
                    { value: 0.5, color: colors.graphs.survey.answers.veryDissatisfied },
                    { value: 1, color: colors.graphs.survey.answers.dissatisfied },
                    { value: 4, color: colors.graphs.survey.answers.neutral },
                    { value: 20, color: colors.graphs.survey.answers.satisfied },
                    { value: 75, color: colors.graphs.survey.answers.verySatisfied, valueText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                ]} />

                <SegmentRatioBar  legend='none' showValueText showValueDecimals data={[
                    { value: 0.5, color: colors.graphs.survey.answers.veryDissatisfied },
                    { value: 1, color: colors.graphs.survey.answers.dissatisfied },
                    { value: 4, color: colors.graphs.survey.answers.neutral },
                    { value: 20, color: colors.graphs.survey.answers.satisfied },
                    { value: 75, color: colors.graphs.survey.answers.verySatisfied, valueText: <span style={{ color: 'white' }}>XX%</span>, tootip: <span>hello</span> }
                ]} />

                <SegmentRatioBar showValueText showValueDecimals data={[
                    { value: 0.5, color: colors.graphs.survey.answers.veryDissatisfied },
                    { value: 1, color: colors.graphs.survey.answers.dissatisfied },
                    { value: 4, color: colors.graphs.survey.answers.neutral },
                    { value: 20, color: colors.graphs.survey.answers.satisfied },
                    { value: 75, color: colors.graphs.survey.answers.verySatisfied, tootip: <span>hello</span> }
                ]} />
            </BorderRow>

            <BorderRow>
                <H3>AnswerGraphics</H3>
                <AnswerGraphics data={{
                    stats: [
                        { date: new Date(2023, 0, 0, 1), totalStartedAnswers: 0, totalFinishedAnswers: 0 },
                        { date: new Date(2023, 0, 1, 1), totalStartedAnswers: 5, totalFinishedAnswers: 0 },
                        { date: new Date(2023, 0, 2, 1), totalStartedAnswers: 10, totalFinishedAnswers: 5 },
                        { date: new Date(2023, 0, 3, 1), totalStartedAnswers: 20, totalFinishedAnswers: 20 }
                    ]
                }} graphWidthPx={400} graphHeightPx={300} />
            </BorderRow>

            <BorderRow>
                <H3>ActivityYearRow</H3>

                {/* Before graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(-2).date, end: graphStart.addMonths(-1).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start before- end less than one month in- graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(-1).date, end: graphStart.addDays(10).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start before- more than one month in- graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(-1).date, end: graphStart.addDays(40).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start before- more than two months in- graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(-1).date, end: graphStart.addDays(70).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start in- end less than one month in- graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addDays(10).date, end: graphStart.addDays(15).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start in- end more than one month in- graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addDays(10).date, end: graphStart.addDays(50).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start in- end more than two months in- graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addDays(15).date, end: graphStart.addDays(85).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start more than two months from end of- end after graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(9).addDays(10).date, end: graphStart.addYears(2).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start less than two months from end of- end after graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(10).addDays(10).date, end: graphStart.addYears(2).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

                {/* Start less than one months from end of- end after graph */}
                <TimelineGraphYearRow graphWidthPx={480} rowHeightPx={25} yearMonthStart={graphStartWithOffset.date} color={colors.graphs.activityTimeline.getColor('AnnualWorkgroupStatus')}
                    activities={[{ id: rng.getRandomId(), linkUrl: '/', start: graphStart.addMonths(11).addDays(10).date, end: graphStart.addYears(2).date, title: 'An A activity', description: 'Lorem ipsum...' }]} />

            </BorderRow>

            <BorderRow>
                <H3>ActivityTimeline</H3>
                <ActivityGraph rowHeightPx={50} yearMonthStart={graphStart.date}
                    activities={activities} />
            </BorderRow>


            <BorderRow>
                <H3>Radials</H3>
                <Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialPieChart value="" segments={[
                            { value:1, color: colors.radialChart.red },
                            { value:1, color: colors.radialChart.green },
                            { value:1, color: colors.radialChart.yellow },
                            { value:1, color: colors.radialChart.unfilled }
                        ]} />
                        <span>RadialPieChart</span>
                    </Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialChart color="red" fillPct={67} value="67%" />
                        <span>RadialChart</span>
                    </Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialAvgChart avg={3.7} />
                        <span>RadialAvgChart</span>
                    </Flex>
                    <Flex flexDirection="column" alignItems="center" gap={4}>
                        <RadialPctChart pct={85} />
                        <span>RadialPctChart</span>
                    </Flex>
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>CompletionPanel</H3>

                30
                50
                70
                90

                <Flex gap={32}>
                    <CompletionPanel
                        pct={24}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={48}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={64}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={80}
                        completionText="Her er en completionText"
                        />
                    <CompletionPanel
                        pct={96}
                        completionText="En anden completionText"
                        missingText="...og en missingText"
                    />
                </Flex>

            </BorderRow>

        </Flex>
    );
};